import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { FaTimes } from 'react-icons/fa';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ReceiptGallery from 'src/components/project-billing/pages/ReceiptViewer';
import Receipt from 'src/components/project-billing/models/Receipt';
import DocumentSourceType from 'src/components/project-billing/models/DocumentSourceType';

interface IReceiptViewerDialogProps {
    dialogName: string;
    handleConfirm?: () => void;
    hideDialog: () => void;
    selectedClient?: number;
    selectedPayPeriod?: string;
    hasProjects?: boolean;
    label?: string;
    context?: number;
    receipts?: Array<Receipt>;
    imageBase64?: string;
    pdfBytes?: Uint8Array;
    // Which backend a full-size view fetch should hit - defaults to OfficeExpenseReceipt so the
    // existing caller (TimeAndExpensePageComponent.tsx) is unaffected without passing this.
    sourceType?: DocumentSourceType;
}

const ReceiptViewerDialog = (props: IReceiptViewerDialogProps) => {
    const [pdfBytes, setPdfBytes] = useState<Uint8Array>(null);
    const [viewingInvoices, setViewingInvoices] = useState<boolean>(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [selectedThumbnail, setSelectedThumbnail] = useState<number>(undefined);
    // True while the selected thumbnail's full-size bytes are being fetched - the network wait
    // ReceiptGallery's own "Loading PDF" animation needs to cover (see its loading prop).
    const [loadingFullSize, setLoadingFullSize] = useState<boolean>(false);
    const { get: getBinary, response: responseBinary } = useFetch('/v1.0', {
        responseType: 'arrayBuffer' // only applies to this instance
      });

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (!!props?.receipts && Array.isArray(props?.receipts) && props?.receipts.length > 0) {
            if (props?.receipts[0]?.base64InvoiceThumbnailString) {
                setViewingInvoices(true);
            }
            setSelectedThumbnail(undefined);
        }
    }, [props?.receipts]);

    useEffect(() => {
        const isUint8 =
            props?.pdfBytes &&
            typeof props.pdfBytes === 'object' &&
            typeof props.pdfBytes.byteLength === 'number' &&
            typeof props.pdfBytes.slice === 'function';
        if (isUint8) {
            setPdfBytes(props.pdfBytes);
        }
    }, [props?.pdfBytes]);

    useEffect(() => {
        if (!!props?.imageBase64) {            
            setImageSrc(props?.imageBase64);
        }
    }, [props?.imageBase64]);

    useEffect(() => {
        if (selectedThumbnail != null) {
            const receipt = props?.receipts?.[selectedThumbnail];

            if (viewingInvoices && receipt?.s3Key) {
                downloadInvoicePdfBytes(receipt.s3Key);
            } else if (!viewingInvoices) {
                getFullSizeReceipt();
            }
        }
    }, [selectedThumbnail]);

    const onCreate = () => {
        console.log('DefineBudgetCategoryDialog onCreate()');
    };

    const OKClicked = () => {
        props.handleConfirm();
    };

    const getJwtToken = (): string => {
        return localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
    };

    const downloadInvoicePdfBytes = async (s3Key: string): Promise<Uint8Array | null> => {
        setLoadingFullSize(true);
        try {
            // Encode the S3 key in the URL
            const baseUrl = process.env.REACT_APP_BASE_API_URL;
            const url = `${baseUrl}/v1.0/s3bucket/${encodeURIComponent(s3Key)}`;

            // Make the fetch request
            const result = await fetch(url, {
                method: 'GET',
                headers: {
                Authorization: `Bearer ${getJwtToken()}`
                }
            });

            // Check for success
            if (!result.ok) {
                console.log('Download failed', result.statusText);
                return null;
            }

            // Convert response to Uint8Array
            const arrayBuffer = await result.arrayBuffer();
            const invoicePdfBytes = new Uint8Array(arrayBuffer);
            setPdfBytes(invoicePdfBytes);
            return new Uint8Array(arrayBuffer);
        } finally {
            setLoadingFullSize(false);
        }
    };

    // Which endpoint backs the full-size view depends on where this list of receipts came from -
    // OFFICE_RECEIPTS (keyed by OF_ID/OR_ID, the historical/default case) or EXPENSE_RECEIPTS (keyed
    // by ER_ID alone, the checklist's landman/period receipts). Add a branch here as the viewer
    // grows to support more document sources.
    const getFullSizeReceipt = async () => {
        if (!props?.receipts || props.receipts.length <= selectedThumbnail) {
            return;
        }
        const receipt = props.receipts[selectedThumbnail];
        const sourceType = props?.sourceType ?? DocumentSourceType.OfficeExpenseReceipt;

        let url: string;
        if (sourceType === DocumentSourceType.TimeExpenseReceipt) {
            url = `/AccountsPayable/timeandexpense/receipts/${receipt.id}`;
        } else if (!!receipt.expenseRecordId) {
            url = `/AccountsPayable/receipts/expense/${receipt.expenseRecordId}/receipt/${receipt.id}`;
        } else {
            return;
        }

        setLoadingFullSize(true);
        try {
            let result = await getBinary(url);

            if (responseBinary.ok && result?.byteLength > 0) {
                setPdfBytes(result);
            } else {
                console.log('Unable to get receipt pdf binary data!!!');
            }
        } finally {
            setLoadingFullSize(false);
        }
    };

    return (
        <div className={'receipt-viewer'}>
            <Dialog
                title={ props?.context === 1 ? 'Expense Receipts' : (props?.context === 2 ? 'Invoice Group Cover Letter Preview' : '')}
                dialogName={props.dialogName}
                isExitButtonInvisible={false}
                exitIcon={<FaTimes />}
                bodyStyle={{ width: '50%', background: 'transparent', height: '98vh', maxWidth: '75vw', padding: '0px' }}
            >
                <div style={{ width: '100%', height: 'auto', display: 'flex', justifyContent: 'center', flexFlow: 'column' }} className={'expense-background'}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <ReceiptGallery
                        pdfBytes={pdfBytes}
                        imageSrc={imageSrc}
                        receiptThumbnails={props?.receipts}
                        setSelectedThumbnail={setSelectedThumbnail}
                        loading={loadingFullSize}
                    />
                </div>
                <div className={'form-row'} style={{background: 'transparent', marginTop: '0px', justifyContent: 'center'}}>
                    <Button
                        className={'blue-hover-button'}
                        style={{ border: 'solid rgb(99, 147, 193) 2px', marginTop: '3px', width: '15%' }}
                        text={'Exit'}
                        onClick={() => OKClicked()}
                    />
                </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ReceiptViewerDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/ReceiptViewerDialog.tsx